import isoLanguages from '@cospired/i18n-iso-languages';
import {
    ZfBanner,
    ZfDialog,
    ZfDialogContent,
    ZfDialogFooter,
    ZfDialogHeader,
    ZfFormField,
    ZfInput,
    ZfLoadingSpinner,
    ZfSelectOption,
    ZfTextButton,
} from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useClient,
    useDownloadClientPackage,
    useDownloadPreview,
    useFinalizeClientPackage,
    useGeneralProjectionSettings,
    useUpdateClientPackage,
} from '../../api';
import type { ClientPackageDto } from '../../api/generated/client';
import { useClientPackageImageRendering } from '../../hooks/useClientPackageImageRendering';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { AVAILABLE_LANGUAGES } from '../../i18n/i18nextInit';
import type { IPackage } from '../../interfaces/package';
import { wnSendTrace } from '../../utils/wn-event-utils';
import { ChapterExporter } from './ChapterExporter';
import { ClientPackageAppendixSelector } from './ClientPackageAppendixSelector';
import { ClientPackageChapterSelector } from './ClientPackageChapterSelector';
import { ClientPackageDocSelector } from './ClientPackageDocSelector';
import { ClientPackageGenericDocumentsSelector } from './ClientPackageGenericDocumentsSelector';

interface IProps {
    displayedClientPackage: ClientPackageDto;
    onConfirmFinalize: (callbackFn: () => void) => void;
}

export const ClientPackageEditor: FunctionComponent<IProps> = ({ displayedClientPackage, onConfirmFinalize }) => {
    const { t, i18n } = useTranslation();
    const { navigate } = useWealthPlanningNavigate();

    const { data: client } = useClient();
    const updateClientPackage = useUpdateClientPackage();
    const downloadPreview = useDownloadPreview();
    const downloadPackage = useDownloadClientPackage();
    const finalizePackage = useFinalizeClientPackage();
    const { data: generalSettings } = useGeneralProjectionSettings();

    const [clientPackage, setClientPackage] = useState<ClientPackageDto>(displayedClientPackage);
    const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const updateClientPackageState = useCallback(
        (updatedPackage: Partial<ClientPackageDto>) => setClientPackage({ ...clientPackage, ...updatedPackage }),
        [clientPackage],
    );

    const isPackageReadonly = useMemo(() => clientPackage?.status !== 'DRAFT', [clientPackage?.status]);

    const { isImageRendering, addImageRenderingDom, setImagesForRenderingEvent } = useClientPackageImageRendering(clientPackage, updateClientPackageState, {
        projectionYears: generalSettings?.projectionPeriodYears ?? 0,
        partnerUuid: client?.partnerUuid ?? '',
    });

    const { isPreviewInProgress, isFinaliseInProgress } = useMemo(
        () => ({
            isPreviewInProgress: downloadPreview.isPending || (isImageRendering.isRendering && isImageRendering.event === 'preview'),
            isFinaliseInProgress: finalizePackage.isPending || (isImageRendering.isRendering && isImageRendering.event === 'finalise'),
        }),
        [isImageRendering, downloadPreview.isPending, finalizePackage.isPending],
    );

    const validate = (isNameRequired = false) => {
        setFormSubmitted(true);
        const isValid = isNameRequired ? !!clientPackage.name : true;
        setIsFormValid(isValid);

        return isValid;
    };

    const onPreviewPackageClick = async () => {
        if (validate()) {
            await setImagesForRenderingEvent('preview');
            await downloadPreview.mutateAsync(clientPackage);
            wnSendTrace({
                feature: 'client-package',
                tags: { partnerUuid: client?.partnerUuid ?? '', action: 'preview-client-package', clientPackageId: `${clientPackage.id}` },
            });
        }
    };

    const onFinalisePackageClick = async () => {
        if (validate(true)) {
            await setImagesForRenderingEvent('finalise');
            onConfirmFinalize(async () => {
                await finalizePackage.mutateAsync(clientPackage);
                navigate(`/${client?.partnerUuid}/documents`);
            });
            wnSendTrace({
                feature: 'client-package',
                tags: { partnerUuid: client?.partnerUuid ?? '', action: 'finalize-client-package', clientPackageId: `${clientPackage.id}` },
            });
        }
    };

    const onDownloadPackageClick = () => downloadPackage.mutateAsync(clientPackage.id!);

    const onCloseEditorDialog = (_event?: CustomEvent<void>, reason?: string) => {
        if (reason === 'backdropClick') return;

        if (!isPackageReadonly && validate()) {
            updateClientPackage.mutateAsync(clientPackage); // name is not required for saving/closing the dialog
        }

        navigate(`/${client?.partnerUuid}/documents`);
    };

    return (
        <>
            <ChapterExporter
                availableLanguage={clientPackage.language!}
                clientPackageChapters={clientPackage.clientPackageChapters}
                onImageRendering={addImageRenderingDom}
                projectionYears={generalSettings?.projectionPeriodYears ?? 0}
            />
            <ZfDialog show={true} disableLightDismiss={true} onDialogClose={onCloseEditorDialog}>
                <ZfDialogHeader>{t('clientPackageTitle')}</ZfDialogHeader>
                <ZfDialogContent>
                    <div className="client-package-editor__content">
                        <ZfFormField label={t('clientPackageInputLanguage')}>
                            <ZfInput
                                type="select"
                                value={clientPackage.language}
                                onSelectChange={(event) => updateClientPackageState({ language: event.detail as IPackage['language'] })}
                                readonly={isPackageReadonly}
                            >
                                {AVAILABLE_LANGUAGES.map((lang) => (
                                    <ZfSelectOption key={lang} value={lang} description={isoLanguages.getName(lang, i18n.language)} />
                                ))}
                            </ZfInput>
                        </ZfFormField>
                        <ZfFormField label={t('clientPackageInputTitle')} mandatory={true}>
                            <ZfInput
                                value={clientPackage.name}
                                onInputChange={(event) => updateClientPackageState({ name: String(event.detail) })}
                                counter={true}
                                maxLength={100}
                                error={isFormSubmitted && !isFormValid}
                                readonly={isPackageReadonly}
                            />
                        </ZfFormField>
                        <ZfFormField label={t('clientPackageInputComment')}>
                            <ZfInput
                                value={clientPackage.comment}
                                placeholder={isPackageReadonly ? t('clientPackageInputNoComment') : ''}
                                onInputChange={(event) => updateClientPackageState({ comment: event.detail })}
                                readonly={isPackageReadonly}
                                multiline={true}
                                counter={true}
                                maxLength={200}
                                rows={2}
                            />
                        </ZfFormField>
                        <ClientPackageChapterSelector
                            clientPackage={clientPackage}
                            disabled={isPackageReadonly}
                            updateClientPackage={updateClientPackageState}
                        />
                        <ClientPackageAppendixSelector
                            clientPackage={clientPackage}
                            disabled={isPackageReadonly}
                            updateClientPackage={updateClientPackageState}
                        />
                        <ClientPackageGenericDocumentsSelector
                            clientPackage={clientPackage}
                            disabled={isPackageReadonly}
                            updateClientPackage={updateClientPackageState}
                        />
                        {clientPackage.clientPackageDocuments?.length > 0 && (
                            <ClientPackageDocSelector
                                clientPackage={clientPackage}
                                disabled={isPackageReadonly}
                                updateClientPackage={updateClientPackageState}
                            />
                        )}
                        {isFormSubmitted && !isFormValid && (
                            <ZfBanner type="error" dismissible={false}>
                                {t('clientPackageInvalidInputsMessage')}
                            </ZfBanner>
                        )}
                        <br />
                    </div>
                </ZfDialogContent>
                <ZfDialogFooter>
                    <div slot="primary">
                        {clientPackage.status === 'DRAFT' && (
                            <ZfTextButton
                                hierarchy="primary"
                                size="medium"
                                style={{ width: 145 }}
                                disabled={isFinaliseInProgress}
                                onClick={onFinalisePackageClick}
                            >
                                {isFinaliseInProgress ? <ZfLoadingSpinner size="medium" /> : t('clientPackageActionFinalise')}
                            </ZfTextButton>
                        )}
                        {clientPackage.status === 'FINALIZED' && (
                            <ZfTextButton hierarchy="secondary" size="medium" leadingIcon="download" onClick={onDownloadPackageClick}>
                                {t('clientPackageActionDownload')}
                            </ZfTextButton>
                        )}
                    </div>
                    <div slot="secondary">
                        {clientPackage.status === 'DRAFT' && (
                            <>
                                <ZfTextButton
                                    hierarchy="secondary"
                                    size="medium"
                                    style={{ width: 180 }}
                                    leadingIcon={isPreviewInProgress ? '' : 'download'}
                                    disabled={isPreviewInProgress}
                                    onClick={onPreviewPackageClick}
                                >
                                    {isPreviewInProgress ? <ZfLoadingSpinner size="medium" /> : t('clientPackageActionPreview')}
                                </ZfTextButton>
                            </>
                        )}
                    </div>
                </ZfDialogFooter>
            </ZfDialog>
        </>
    );
};
